const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAYS_HEADER = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

const WEEKDAY_SLOTS = ['07:00','07:30','08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30'];
const SATURDAY_SLOTS: string[] = [];

interface Props {
  occupiedMap: Record<string, string[]>;
  selectedDate: string | null;
  onSelect: (date: string) => void;
}

function getSlots(dateStr: string): string[] {
  const dow = new Date(dateStr + 'T12:00:00').getDay();
  if (dow === 0 || dow === 6) return []; // Sunday and Saturday closed
  return WEEKDAY_SLOTS;
}

function isPastDate(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dateStr + 'T00:00:00') < today;
}

function isWeekend(dateStr: string): boolean {
  const dow = new Date(dateStr + 'T12:00:00').getDay();
  return dow === 0 || dow === 6;
}

function availableCount(dateStr: string, occupiedMap: Record<string, string[]>): number {
  const slots = getSlots(dateStr);
  if (slots.length === 0) return 0;
  const today = new Date();
  const isToday = dateStr === today.toISOString().split('T')[0];
  const nowMin = today.getHours() * 60 + today.getMinutes();
  const occupied = occupiedMap[dateStr] ?? [];
  return slots.filter((s) => {
    if (occupied.includes(s)) return false;
    if (isToday) {
      const [h, m] = s.split(':').map(Number);
      if (h * 60 + m <= nowMin + 60) return false;
    }
    return true;
  }).length;
}

const BookingCalendar = ({ occupiedMap, selectedDate, onSelect }: Props) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const fmt = (d: number) => `${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

  const prev = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const next = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 2);
    if (year < maxDate.getFullYear() || (year === maxDate.getFullYear() && month < maxDate.getMonth())) {
      if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1);
    }
  };

  const todayStr = now.toISOString().split('T')[0];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <button onClick={prev} className="w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer">
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <h3 className="text-white font-black text-sm uppercase tracking-widest">{MONTHS[month]} {year}</h3>
        <button onClick={next} className="w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer">
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-2">
        {DAYS_HEADER.map((d) => (
          <div key={d} className={`text-center text-[10px] font-bold uppercase tracking-wider py-2 ${(d === 'Dom' || d === 'Sáb') ? 'text-red-500/50' : 'text-zinc-600'}`}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (!day) return <div key={idx} />;
          const dateStr = fmt(day);
          const past = isPastDate(dateStr);
          const weekend = isWeekend(dateStr);
          const blocked = past || weekend;
          const avail = blocked ? 0 : availableCount(dateStr, occupiedMap);
          const full = !blocked && avail === 0;
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;

          let cellCls = 'aspect-square flex flex-col items-center justify-center transition-all ';
          let numCls = 'text-xs font-bold ';
          let subCls = 'text-[8px] mt-0.5 ';

          if (isSelected) {
            cellCls += 'bg-yellow-400 border border-yellow-400 cursor-pointer';
            numCls += 'text-black';
            subCls += 'text-black/60';
          } else if (blocked) {
            cellCls += 'cursor-not-allowed opacity-30';
            numCls += weekend ? 'text-red-400' : 'text-zinc-600';
            subCls += weekend ? 'text-red-400/50' : 'text-zinc-700';
          } else if (full) {
            cellCls += 'cursor-not-allowed border border-zinc-800 bg-zinc-900/50';
            numCls += 'text-zinc-600';
            subCls += 'text-zinc-700';
          } else {
            cellCls += 'cursor-pointer border border-zinc-800 hover:border-yellow-400/50 hover:bg-yellow-400/5 bg-zinc-900';
            numCls += isToday ? 'text-yellow-400' : 'text-zinc-300';
            subCls += 'text-zinc-600';
          }

          return (
            <button
              key={idx}
              disabled={blocked || full}
              onClick={() => !blocked && !full && onSelect(dateStr)}
              className={cellCls}
            >
              <span className={numCls}>{day}</span>
              {!blocked && (
                <span className={subCls}>
                  {full ? 'Lleno' : `${avail}h`}
                </span>
              )}
              {weekend && <span className={subCls}>Cerrado</span>}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-zinc-800">
        {[
          { color: 'bg-yellow-400', label: 'Seleccionado' },
          { color: 'bg-zinc-800 border border-zinc-700', label: 'Disponible' },
          { color: 'bg-zinc-900 opacity-30', label: 'No disponible' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm ${color}`} />
            <span className="text-zinc-600 text-[10px]">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

import { useState } from 'react';
export default BookingCalendar;
